import { useEffect, useMemo, useState } from "react";
import { api, API, ROLE_LABEL, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import FileUpload, { fileDownloadUrl } from "@/components/FileUpload";
import {
  Users, Briefcase, FileText, ClipboardCheck, Plus, X, ChevronRight,
  Trash2, Send, Award, UserCheck, Calendar as CalIcon, Building2, Mail, Phone,
  FileDown, Circle, CheckCircle2, RotateCcw,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: ClipboardCheck },
  { id: "employees", label: "Employees", icon: Users },
  { id: "postings", label: "Postings", icon: Briefcase },
  { id: "applications", label: "Applications", icon: FileText },
  { id: "onboarding", label: "Onboarding", icon: UserCheck },
];

const DEPTS = [
  ["GOV", "Governance & Administration"],
  ["HTH", "Health Services"],
  ["EDU", "Education"],
  ["HSG", "Housing & Public Works"],
  ["CAP", "Capital Projects"],
  ["LND", "Lands & Resources"],
  ["SOC", "Social Services"],
  ["ECO", "Economic Development"],
  ["CUL", "Culture & Language"],
];

const EMP_STATUS_STYLE = {
  active:     { label: "Active",     color: "#89b4fa" },
  probation:  { label: "Probation",  color: "#d4b876" },
  on_leave:   { label: "On leave",   color: "#a6adc8" },
  terminated: { label: "Terminated", color: "#7f849c" },
};
const POSTING_STATUS_STYLE = {
  draft:        { label: "Draft",        color: "#a6adc8" },
  open:         { label: "Open",         color: "#89b4fa" },
  in_selection: { label: "In selection", color: "#d4b876" },
  filled:       { label: "Filled",       color: "#89b4fa" },
  withdrawn:    { label: "Withdrawn",    color: "#7f849c" },
};
const APP_STATUS_STYLE = {
  submitted:    { label: "Submitted",    color: "#a6adc8" },
  shortlisted: { label: "Shortlisted",  color: "#d4b876" },
  selected:     { label: "Selected",     color: "#89b4fa" },
  not_selected: { label: "Not selected", color: "#7f849c" },
  withdrawn:    { label: "Withdrawn",    color: "#7f849c" },
};

export default function HRM() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [postings, setPostings] = useState([]);
  const [onboardings, setOnboardings] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [openEmp, setOpenEmp] = useState(null);
  const [creatingEmp, setCreatingEmp] = useState(false);
  const [openPost, setOpenPost] = useState(null);
  const [creatingPost, setCreatingPost] = useState(false);
  const [openOnb, setOpenOnb] = useState(null);

  const isAdmin = user?.is_super_admin || user?.is_owner || ["chief","deputy_chief","executive_director"].includes(user?.role);

  const load = async () => {
    const [d, e, p, o] = await Promise.all([
      api.get("/hrm/dashboard").then(r => r.data).catch(() => null),
      api.get("/hrm/employees").then(r => r.data).catch(() => []),
      api.get("/hrm/postings?include_all=true").then(r => r.data).catch(() => []),
      api.get("/hrm/onboarding").then(r => r.data).catch(() => []),
    ]);
    setDashboard(d);
    setEmployees(e);
    setPostings(p);
    setOnboardings(o);
    // For the flat applications tab, pull one page per posting so we can present the cross-cutting view.
    // In production this would be a proper /api/hrm/applications endpoint; kept simple here since the
    // per-posting drill-down is where HR actually operates.
    const allDropped = [];
    for (const post of p) {
      if (isAdmin) {
        try {
          const r = await api.get(`/hrm/postings/${post.id}/applications`);
          for (const a of r.data) allDropped.push({ ...a });
        } catch { /* ignore per-posting fetch */ }
      }
    }
    setAllApps(allDropped);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">Human resources</div>
          <h1 className="font-display text-4xl mt-2 leading-tight">HR workbench</h1>
          <p className="text-white/55 mt-3 max-w-xl text-sm leading-relaxed">
            Employee folders, job postings, applications, and onboarding — one workspace. Every action
            here writes to the record and updates the public staff directory automatically.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8 border-b border-white/10 flex gap-1 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id} onClick={() => setTab(t.id)}
            data-testid={`hrm-tab-${t.id}`}
            className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest border-b -mb-px flex items-center gap-2 ${
              tab === t.id
                ? "border-[color:var(--cobalt)] text-[color:var(--cobalt)]"
                : "border-transparent text-white/55 hover:text-white"
            }`}
          >
            <t.icon size={12}/>{t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview dashboard={dashboard} employees={employees} postings={postings} onboardings={onboardings}/>}

      {tab === "employees" && (
        <EmployeesTab
          isAdmin={isAdmin} employees={employees}
          onOpen={setOpenEmp}
          onCreate={() => setCreatingEmp(true)}
        />
      )}

      {tab === "postings" && (
        <PostingsTab
          isAdmin={isAdmin} postings={postings}
          onOpen={setOpenPost}
          onCreate={() => setCreatingPost(true)}
        />
      )}

      {tab === "applications" && (
        <ApplicationsTab applications={allApps} onOpen={(a) => setOpenPost(a.posting_id)}/>
      )}

      {tab === "onboarding" && (
        <OnboardingTab onboardings={onboardings} onOpen={setOpenOnb}/>
      )}

      {openEmp && (
        <EmployeeFolderModal
          isAdmin={isAdmin} eid={openEmp}
          onClose={() => { setOpenEmp(null); load(); }}
        />
      )}
      {creatingEmp && (
        <EmployeeCreateModal onClose={() => { setCreatingEmp(false); load(); }} />
      )}
      {openPost && (
        <PostingDrawer
          isAdmin={isAdmin} pid={openPost}
          onClose={() => { setOpenPost(null); load(); }}
          onSelected={() => load()}
        />
      )}
      {creatingPost && (
        <PostingCreateModal onClose={() => { setCreatingPost(false); load(); }} />
      )}
      {openOnb && (
        <OnboardingDrawer isAdmin={isAdmin} oid={openOnb} onClose={() => { setOpenOnb(null); load(); }} />
      )}
    </div>
  );
}

/* ================= OVERVIEW ================= */

function Overview({ dashboard, employees, postings, onboardings }) {
  const probationSoon = employees.filter(e => e.status === "probation" && e.probation_end).slice(0, 4);
  const recentPostings = postings.slice(0, 5);
  return (
    <div className="mt-8">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi label="Active staff" value={dashboard?.employees_active ?? "—"} testid="hrm-kpi-active" color="#89b4fa"/>
        <Kpi label="On probation" value={dashboard?.employees_probation ?? "—"} testid="hrm-kpi-probation" color="#d4b876"/>
        <Kpi label="Ends in 30d" value={dashboard?.probation_end_30d ?? "—"} testid="hrm-kpi-prob30"/>
        <Kpi label="Postings open" value={dashboard?.postings_open ?? "—"} testid="hrm-kpi-open" color="#89b4fa"/>
        <Kpi label="In selection" value={dashboard?.postings_in_selection ?? "—"} testid="hrm-kpi-sel" color="#d4b876"/>
        <Kpi label="Onboarding" value={dashboard?.onboarding_in_progress ?? "—"} testid="hrm-kpi-onboarding"/>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="panel p-5">
          <div className="label-mono mb-4">Probation ending in 30 days</div>
          {probationSoon.length === 0 ? (
            <div className="font-mono text-xs text-white/40">Nothing on the horizon.</div>
          ) : (
            <div className="space-y-2">
              {probationSoon.map(e => (
                <div key={e.id} className="flex justify-between border border-white/8 p-3 text-sm">
                  <div>
                    <div className="font-display">{e.preferred_name || `${e.legal_first_name} ${e.legal_last_name}`}</div>
                    <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest">{e.position_title} · {e.department}</div>
                  </div>
                  <div className="font-mono text-[10px] text-[color:var(--cobalt)]">{new Date(e.probation_end).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="panel p-5">
          <div className="label-mono mb-4">Recent postings</div>
          {recentPostings.length === 0 ? (
            <div className="font-mono text-xs text-white/40">No postings yet. Create the first from the Postings tab.</div>
          ) : (
            <div className="space-y-2">
              {recentPostings.map(p => {
                const st = POSTING_STATUS_STYLE[p.status] || {};
                return (
                  <div key={p.id} className="flex justify-between border border-white/8 p-3 text-sm">
                    <div className="min-w-0">
                      <div className="font-display truncate">{p.title}</div>
                      <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest">{p.department} · {p.application_count} applications</div>
                    </div>
                    <span className="tag whitespace-nowrap" style={{color: st.color}}>{st.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function Kpi({ label, value, color, testid }) {
  return (
    <div className="border border-white/8 bg-white/[0.015] p-4 corner-tick" data-testid={testid}>
      <div className="font-display text-3xl" style={{color: color || "#cdd6f4"}}>{value}</div>
      <div className="label-mono mt-2">{label}</div>
    </div>
  );
}

/* ================= EMPLOYEES ================= */

function EmployeesTab({ isAdmin, employees, onOpen, onCreate }) {
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [status, setStatus] = useState("");
  const filtered = useMemo(() => {
    return employees.filter(e => {
      const name = `${e.legal_first_name || ""} ${e.legal_last_name || ""} ${e.preferred_name || ""}`.toLowerCase();
      if (q && !name.includes(q.toLowerCase()) && !((e.position_title || "").toLowerCase().includes(q.toLowerCase()))) return false;
      if (dept && e.department !== dept) return false;
      if (status && e.status !== status) return false;
      return true;
    });
  }, [employees, q, dept, status]);
  return (
    <div className="mt-8">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search name or title"
          className="px-3 py-2 text-sm w-64" data-testid="hrm-emp-search"/>
        <select value={dept} onChange={e=>setDept(e.target.value)} className="px-3 py-2 text-sm" data-testid="hrm-emp-dept">
          <option value="">All departments</option>
          {DEPTS.map(([c,l]) => <option key={c} value={c}>{l}</option>)}
        </select>
        <select value={status} onChange={e=>setStatus(e.target.value)} className="px-3 py-2 text-sm" data-testid="hrm-emp-status">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="probation">Probation</option>
          <option value="on_leave">On leave</option>
          <option value="terminated">Terminated</option>
        </select>
        {isAdmin && (
          <button onClick={onCreate} className="btn-cta primary ml-auto text-xs" data-testid="hrm-new-emp">
            <Plus size={12}/> New employee
          </button>
        )}
      </div>
      {filtered.length === 0 ? (
        <div className="border border-white/8 bg-white/[0.015] p-8 text-center">
          <div className="font-display text-xl">No employees yet.</div>
          <div className="text-white/50 text-sm mt-2">
            {isAdmin ? "Add the first employee to seed the staff directory." : "HR hasn't published any employee records yet."}
          </div>
        </div>
      ) : (
        <div className="border border-white/8">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03]">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Position</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Hired</th>
                <th className="px-4 py-3"/>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => {
                const st = EMP_STATUS_STYLE[e.status] || {};
                return (
                  <tr key={e.id} className="border-t border-white/8 hover:bg-white/[0.02] cursor-pointer" onClick={()=>onOpen(e.id)} data-testid={`emp-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-display">{e.preferred_name || `${e.legal_first_name} ${e.legal_last_name}`}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{e.position_title}</td>
                    <td className="px-4 py-3 font-mono text-xs text-white/70">{e.department}</td>
                    <td className="px-4 py-3 font-mono text-[10px] text-white/60 uppercase">{(e.employment_type || "").replace("_"," ")}</td>
                    <td className="px-4 py-3"><span className="tag" style={{color: st.color}}>{st.label}</span></td>
                    <td className="px-4 py-3 font-mono text-[10px] text-white/50">{e.hire_date}</td>
                    <td className="px-4 py-3"><ChevronRight size={14} className="text-white/30"/></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EmployeeCreateModal({ onClose }) {
  const [form, setForm] = useState({
    legal_first_name: "", legal_last_name: "", preferred_name: "",
    position_title: "", department: "GOV", hire_date: new Date().toISOString().slice(0,10),
    employment_type: "permanent_ft", status: "active",
    work_email: "", work_phone: "", office_location: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/hrm/employees", form);
      toast.success("Employee added");
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 overflow-y-auto p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={submit}
        className="max-w-2xl mx-auto frost-glass border border-white/12 p-6 my-12" data-testid="hrm-emp-create-modal">
        <div className="label-mono">New employee record</div>
        <h2 className="font-display text-2xl mt-2">Create digital folder</h2>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <Field label="Legal first name" required>
            <input required value={form.legal_first_name} onChange={e=>setForm({...form, legal_first_name: e.target.value})} className="w-full px-3 py-2 text-sm" data-testid="emp-fn"/>
          </Field>
          <Field label="Legal last name" required>
            <input required value={form.legal_last_name} onChange={e=>setForm({...form, legal_last_name: e.target.value})} className="w-full px-3 py-2 text-sm" data-testid="emp-ln"/>
          </Field>
          <Field label="Preferred name" cls="col-span-2">
            <input value={form.preferred_name} onChange={e=>setForm({...form, preferred_name: e.target.value})} className="w-full px-3 py-2 text-sm"/>
          </Field>
          <Field label="Position title" required cls="col-span-2">
            <input required value={form.position_title} onChange={e=>setForm({...form, position_title: e.target.value})} className="w-full px-3 py-2 text-sm" data-testid="emp-title"/>
          </Field>
          <Field label="Department" required>
            <select value={form.department} onChange={e=>setForm({...form, department: e.target.value})} className="w-full px-3 py-2 text-sm" data-testid="emp-dept">
              {DEPTS.map(([c,l]) => <option key={c} value={c}>{l}</option>)}
            </select>
          </Field>
          <Field label="Employment type">
            <select value={form.employment_type} onChange={e=>setForm({...form, employment_type: e.target.value})} className="w-full px-3 py-2 text-sm">
              <option value="permanent_ft">Permanent full-time</option>
              <option value="permanent_pt">Permanent part-time</option>
              <option value="term">Term</option>
              <option value="casual">Casual</option>
              <option value="contract">Contract</option>
            </select>
          </Field>
          <Field label="Hire date" required>
            <input type="date" required value={form.hire_date} onChange={e=>setForm({...form, hire_date: e.target.value})} className="w-full px-3 py-2 text-sm"/>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={e=>setForm({...form, status: e.target.value})} className="w-full px-3 py-2 text-sm">
              <option value="active">Active</option>
              <option value="probation">Probation</option>
            </select>
          </Field>
          <Field label="Work email" cls="col-span-2">
            <input type="email" value={form.work_email} onChange={e=>setForm({...form, work_email: e.target.value})} className="w-full px-3 py-2 text-sm"/>
          </Field>
          <Field label="Work phone">
            <input value={form.work_phone} onChange={e=>setForm({...form, work_phone: e.target.value})} className="w-full px-3 py-2 text-sm"/>
          </Field>
          <Field label="Office location">
            <input value={form.office_location} onChange={e=>setForm({...form, office_location: e.target.value})} placeholder="e.g. Band Office · Room 12" className="w-full px-3 py-2 text-sm"/>
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-white/8">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={busy} className="btn-cta primary" data-testid="emp-submit">
            {busy ? "Saving…" : "Create folder"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EmployeeFolderModal({ isAdmin, eid, onClose }) {
  const [emp, setEmp] = useState(null);
  const [tab, setTab] = useState("summary");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState({});
  const [docForm, setDocForm] = useState({ label: "", kind: "other", file: null, inline_text: "" });
  const confirm = useConfirm();

  const load = () => api.get(`/hrm/employees/${eid}`).then(r=>setEmp(r.data)).catch(()=>{});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eid]);

  if (!emp) return null;
  const st = EMP_STATUS_STYLE[emp.status] || {};

  const setField = (k, v) => {
    setDirty(d => ({...d, [k]: v}));
    setEmp(cur => ({...cur, [k]: v}));
  };
  const save = async () => {
    if (Object.keys(dirty).length === 0) return;
    setBusy(true);
    try {
      await api.patch(`/hrm/employees/${eid}`, dirty);
      setDirty({});
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  const attachDoc = async (e) => {
    e.preventDefault();
    if (!docForm.label.trim()) return;
    // Require either an uploaded file OR inline text (backend accepts either)
    if (!docForm.file && !docForm.inline_text.trim()) {
      toast.error("Attach a file or paste inline text.");
      return;
    }
    try {
      await api.post(`/hrm/employees/${eid}/documents`, {
        label: docForm.label,
        kind: docForm.kind,
        url: docForm.file ? `${API}/files/${docForm.file.id}` : "",
        inline_text: docForm.inline_text || undefined,
      });
      toast.success("Document attached");
      setDocForm({ label: "", kind: "other", file: null, inline_text: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Failed"); }
  };
  const removeDoc = async (did) => {
    const ok = await confirm({ title: "Remove document?", destructive: true, message: "This deletes the reference from the employee's folder.", confirmLabel: "Remove" });
    if (!ok) return;
    await api.delete(`/hrm/employees/${eid}/documents/${did}`);
    toast.success("Removed");
    load();
  };
  const terminate = async () => {
    const ok = await confirm({ title: "Terminate employment?", destructive: true, message: `The record for ${emp.legal_first_name} ${emp.legal_last_name} will be marked terminated. History is preserved.`, confirmLabel: "Terminate" });
    if (!ok) return;
    await api.delete(`/hrm/employees/${eid}`);
    toast.success("Employment terminated");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 overflow-y-auto p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="max-w-4xl mx-auto frost-glass border border-white/12 my-8 p-8" data-testid="hrm-emp-modal">
        {/* Header */}
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <div className="label-mono">Digital HR folder</div>
            <h2 className="font-display text-3xl mt-2 leading-tight">
              {emp.preferred_name || `${emp.legal_first_name} ${emp.legal_last_name}`}
            </h2>
            <div className="flex items-center gap-3 mt-2 flex-wrap font-mono text-[10px] text-white/50 uppercase tracking-widest">
              <span className="tag" style={{color: st.color}}>{st.label}</span>
              <span>{emp.position_title}</span>
              <span>·</span>
              <span>{emp.department}</span>
              {emp.hire_date && <><span>·</span><span>hired {emp.hire_date}</span></>}
            </div>
          </div>
          <button onClick={onClose} className="btn-cta text-xs"><X size={12}/></button>
        </div>

        {/* Tabs */}
        <div className="border-b border-white/10 flex gap-1 mt-6">
          {[
            ["summary","Summary"],
            ["payroll","Payroll & tax"],
            ["contact","Contact"],
            ["documents","Documents"],
          ].map(([id,label]) => (
            <button key={id} onClick={()=>setTab(id)}
              className={`px-3 py-2 font-mono text-[11px] uppercase tracking-widest border-b -mb-px ${tab===id ? "border-[color:var(--cobalt)] text-[color:var(--cobalt)]" : "border-transparent text-white/55 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "summary" && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            {isAdmin && (
              <div className="col-span-2">
                <div className="label-mono mb-1">Employee photo</div>
                <div className="flex items-start gap-4">
                  {emp.photo_url && (
                    <img src={emp.photo_url} alt="" className="w-20 h-20 object-cover border border-white/12"/>
                  )}
                  <div className="flex-1">
                    <FileUpload
                      accept=".jpg,.jpeg,.png,.webp"
                      maxSizeMb={5}
                      label="Upload staff photo (JPG or PNG)"
                      value={emp.photo_url ? { id: "photo", original_filename: "Current photo" } : null}
                      onChange={async ({ file }) => {
                        const newUrl = file ? `${API}/files/${file.id}` : null;
                        // Auto-persist immediately — the attached-state UI signal implies saved.
                        try {
                          await api.patch(`/hrm/employees/${eid}`, { photo_url: newUrl });
                          setEmp(cur => ({ ...cur, photo_url: newUrl }));
                          toast.success(file ? "Photo saved" : "Photo removed");
                        } catch (e) {
                          toast.error(formatError(e.response?.data?.detail) || "Failed to save photo");
                        }
                      }}
                      downloadUrl={emp.photo_url}
                      testid="emp-photo-upload"
                    />
                  </div>
                </div>
              </div>
            )}
            <Field label="Legal first name" locked={!isAdmin}><input disabled={!isAdmin} value={emp.legal_first_name || ""} onChange={e=>setField("legal_first_name", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
            <Field label="Legal last name" locked={!isAdmin}><input disabled={!isAdmin} value={emp.legal_last_name || ""} onChange={e=>setField("legal_last_name", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
            <Field label="Preferred name" cls="col-span-2" locked={!isAdmin}><input disabled={!isAdmin} value={emp.preferred_name || ""} onChange={e=>setField("preferred_name", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
            <Field label="Position title" cls="col-span-2" locked={!isAdmin}><input disabled={!isAdmin} value={emp.position_title || ""} onChange={e=>setField("position_title", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
            <Field label="Department" locked={!isAdmin}>
              <select disabled={!isAdmin} value={emp.department || "GOV"} onChange={e=>setField("department", e.target.value)} className="w-full px-3 py-2 text-sm">
                {DEPTS.map(([c,l]) => <option key={c} value={c}>{l}</option>)}
              </select>
            </Field>
            <Field label="Status" locked={!isAdmin}>
              <select disabled={!isAdmin} value={emp.status || "active"} onChange={e=>setField("status", e.target.value)} className="w-full px-3 py-2 text-sm">
                <option value="active">Active</option>
                <option value="probation">Probation</option>
                <option value="on_leave">On leave</option>
                <option value="terminated">Terminated</option>
              </select>
            </Field>
            <Field label="Employment type" locked={!isAdmin}>
              <select disabled={!isAdmin} value={emp.employment_type || "permanent_ft"} onChange={e=>setField("employment_type", e.target.value)} className="w-full px-3 py-2 text-sm">
                <option value="permanent_ft">Permanent FT</option>
                <option value="permanent_pt">Permanent PT</option>
                <option value="term">Term</option>
                <option value="casual">Casual</option>
                <option value="contract">Contract</option>
              </select>
            </Field>
            <Field label="Hire date" locked={!isAdmin}><input type="date" disabled={!isAdmin} value={emp.hire_date || ""} onChange={e=>setField("hire_date", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
            <Field label="Probation end" locked={!isAdmin}><input type="date" disabled={!isAdmin} value={emp.probation_end || ""} onChange={e=>setField("probation_end", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
            <Field label="Reports to" locked={!isAdmin}><input disabled={!isAdmin} value={emp.reports_to || ""} onChange={e=>setField("reports_to", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
          </div>
        )}

        {tab === "payroll" && (
          isAdmin ? (
            <div className="mt-5 grid grid-cols-2 gap-3">
              <Field label="SIN (masked)"><input value={emp.sin || ""} onChange={e=>setField("sin", e.target.value)} placeholder="***-***-XXX (only super admin sees full)" className="w-full px-3 py-2 text-sm font-mono"/></Field>
              <Field label="SIN verified">
                <select value={emp.sin_verified ? "y" : "n"} onChange={e=>setField("sin_verified", e.target.value==="y")} className="w-full px-3 py-2 text-sm">
                  <option value="n">No</option><option value="y">Yes</option>
                </select>
              </Field>
              <Field label="Pay rate"><input type="number" step="0.01" value={emp.pay_rate ?? ""} onChange={e=>setField("pay_rate", e.target.value ? parseFloat(e.target.value) : null)} className="w-full px-3 py-2 text-sm"/></Field>
              <Field label="Pay frequency">
                <select value={emp.pay_frequency || "bi_weekly"} onChange={e=>setField("pay_frequency", e.target.value)} className="w-full px-3 py-2 text-sm">
                  <option value="weekly">Weekly</option>
                  <option value="bi_weekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual salary</option>
                </select>
              </Field>
              <Field label="Employment number"><input value={emp.employment_number || ""} onChange={e=>setField("employment_number", e.target.value)} className="w-full px-3 py-2 text-sm font-mono"/></Field>
              <Field label="GL code"><input value={emp.gl_code || ""} onChange={e=>setField("gl_code", e.target.value)} className="w-full px-3 py-2 text-sm font-mono"/></Field>
              <Field label="TD1 (Federal) on file">
                <select value={emp.td1_federal_on_file ? "y":"n"} onChange={e=>setField("td1_federal_on_file", e.target.value==="y")} className="w-full px-3 py-2 text-sm">
                  <option value="n">No</option><option value="y">Yes</option>
                </select>
              </Field>
              <Field label="TD1 (Provincial) on file">
                <select value={emp.td1_provincial_on_file ? "y":"n"} onChange={e=>setField("td1_provincial_on_file", e.target.value==="y")} className="w-full px-3 py-2 text-sm">
                  <option value="n">No</option><option value="y">Yes</option>
                </select>
              </Field>
              <Field label="CPP exempt (Indian Act 87)">
                <select value={emp.cpp_exempt ? "y":"n"} onChange={e=>setField("cpp_exempt", e.target.value==="y")} className="w-full px-3 py-2 text-sm">
                  <option value="n">No</option><option value="y">Yes</option>
                </select>
              </Field>
              <Field label="EI exempt">
                <select value={emp.ei_exempt ? "y":"n"} onChange={e=>setField("ei_exempt", e.target.value==="y")} className="w-full px-3 py-2 text-sm">
                  <option value="n">No</option><option value="y">Yes</option>
                </select>
              </Field>
              <Field label="Benefit group" cls="col-span-2"><input value={emp.benefit_group || ""} onChange={e=>setField("benefit_group", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
            </div>
          ) : (
            <div className="mt-5 border border-white/8 bg-white/[0.02] p-6 font-mono text-xs text-white/50">
              Payroll and tax fields are restricted to HR administrators (Chief, Deputy Chief, Executive Director).
            </div>
          )
        )}

        {tab === "contact" && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Field label="Work email" cls="col-span-2"><input disabled={!isAdmin} type="email" value={emp.work_email || ""} onChange={e=>setField("work_email", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
            <Field label="Work phone"><input disabled={!isAdmin} value={emp.work_phone || ""} onChange={e=>setField("work_phone", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
            <Field label="Office location"><input disabled={!isAdmin} value={emp.office_location || ""} onChange={e=>setField("office_location", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
            {isAdmin && (
              <>
                <Field label="Personal phone"><input value={emp.phone || ""} onChange={e=>setField("phone", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
                <Field label="Personal email"><input type="email" value={emp.personal_email || ""} onChange={e=>setField("personal_email", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
                <Field label="Address" cls="col-span-2"><textarea rows={2} value={emp.address || ""} onChange={e=>setField("address", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
                <Field label="Emergency contact name"><input value={emp.emergency_contact_name || ""} onChange={e=>setField("emergency_contact_name", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
                <Field label="Emergency contact phone"><input value={emp.emergency_contact_phone || ""} onChange={e=>setField("emergency_contact_phone", e.target.value)} className="w-full px-3 py-2 text-sm"/></Field>
              </>
            )}
          </div>
        )}

        {tab === "documents" && (
          <div className="mt-5">
            {isAdmin && (
              <form onSubmit={attachDoc} className="grid grid-cols-2 gap-3 mb-6 pb-6 border-b border-white/8">
                <Field label="Label" cls="col-span-2">
                  <input required value={docForm.label} onChange={e=>setDocForm({...docForm, label: e.target.value})} placeholder="e.g. Signed offer letter" className="w-full px-3 py-2 text-sm" data-testid="emp-doc-label"/>
                </Field>
                <Field label="Kind">
                  <select value={docForm.kind} onChange={e=>setDocForm({...docForm, kind: e.target.value})} className="w-full px-3 py-2 text-sm">
                    <option value="offer_letter">Offer letter</option>
                    <option value="td1_federal">TD1 (Federal)</option>
                    <option value="td1_provincial">TD1 (Provincial)</option>
                    <option value="void_cheque">Void cheque</option>
                    <option value="resume">Résumé</option>
                    <option value="cover_letter">Cover letter</option>
                    <option value="contract">Contract</option>
                    <option value="policy_ack">Policy acknowledgement</option>
                    <option value="health_card">Health card</option>
                    <option value="confidentiality">Confidentiality</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="URL (optional)" cls="col-span-2">
                  <FileUpload
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.txt,.rtf"
                    maxSizeMb={25}
                    label="Attach a file"
                    value={docForm.file}
                    onChange={({ file }) => setDocForm({...docForm, file})}
                    testid="emp-doc-file"
                  />
                </Field>
                <Field label="Inline text (optional — for short notes)" cls="col-span-2">
                  <textarea rows={2} value={docForm.inline_text} onChange={e=>setDocForm({...docForm, inline_text: e.target.value})} className="w-full px-3 py-2 text-sm"/>
                </Field>
                <div className="col-span-2 flex justify-end">
                  <button type="submit" className="btn-cta text-xs" data-testid="emp-doc-add"><Plus size={12}/> Attach</button>
                </div>
              </form>
            )}
            {(emp.documents || []).length === 0 ? (
              <div className="font-mono text-xs text-white/40">No documents attached.</div>
            ) : (
              <div className="space-y-2">
                {emp.documents.map(d => (
                  <div key={d.id} className="flex justify-between items-start border border-white/8 p-3">
                    <div className="min-w-0">
                      <div className="font-display">{d.label}</div>
                      <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest">{d.kind} · {new Date(d.uploaded_at).toLocaleDateString()}</div>
                      {d.inline_text && <div className="text-white/60 text-sm mt-2 whitespace-pre-line">{d.inline_text}</div>}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="btn-cta text-xs"><FileDown size={12}/> View</a>}
                      {isAdmin && <button onClick={()=>removeDoc(d.id)} className="btn-cta danger text-xs"><Trash2 size={12}/></button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="mt-8 pt-6 border-t border-white/8 flex justify-between">
            <button onClick={terminate} className="btn-cta danger text-xs" data-testid="emp-terminate"><Trash2 size={12}/> Terminate</button>
            <button onClick={save} disabled={busy || Object.keys(dirty).length===0} className="btn-cta primary text-xs" data-testid="emp-save">
              {busy ? "Saving…" : Object.keys(dirty).length ? `Save (${Object.keys(dirty).length})` : "Saved"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= POSTINGS ================= */

function PostingsTab({ isAdmin, postings, onOpen, onCreate }) {
  const [statusFilter, setStatusFilter] = useState("");
  const filtered = statusFilter ? postings.filter(p => p.status === statusFilter) : postings;
  return (
    <div className="mt-8">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="px-3 py-2 text-sm">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="open">Open</option>
          <option value="in_selection">In selection</option>
          <option value="filled">Filled</option>
          <option value="withdrawn">Withdrawn</option>
        </select>
        {isAdmin && (
          <button onClick={onCreate} className="btn-cta primary ml-auto text-xs" data-testid="hrm-new-post">
            <Plus size={12}/> Draft posting
          </button>
        )}
      </div>
      {filtered.length === 0 ? (
        <div className="border border-white/8 bg-white/[0.015] p-8 text-center">
          <div className="font-display text-xl">No postings yet.</div>
          <div className="text-white/50 text-sm mt-2">
            {isAdmin ? "Draft your first posting — deadline, requirements and questions are baked in." : "There are no open positions right now."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => {
            const st = POSTING_STATUS_STYLE[p.status] || {};
            return (
              <button key={p.id} onClick={()=>onOpen(p.id)} className="border border-white/8 bg-white/[0.015] p-5 text-left" data-testid={`post-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <span className="tag" style={{color: st.color}}>{st.label}</span>
                  <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{p.department}</span>
                </div>
                <div className="font-display text-lg mt-3 leading-tight">{p.title}</div>
                <div className="text-white/55 text-xs mt-2 leading-relaxed line-clamp-2">{p.summary}</div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/8 font-mono text-[10px] uppercase tracking-widest text-white/50">
                  <span>{p.application_count || 0} applications</span>
                  <span>closes {p.closes_at ? new Date(p.closes_at).toLocaleDateString() : "—"}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PostingCreateModal({ onClose }) {
  const [form, setForm] = useState({
    title: "", department: "GOV", position_type: "permanent_ft",
    hours_per_week: 40, salary_range: "", location: "Kasabonika Lake, ON",
    summary: "", description: "", responsibilities: "", qualifications: "",
    closes_at: "", require_resume: true, require_cover_letter: true,
    questions: [], preferred_band_member: true,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, closes_at: new Date(form.closes_at).toISOString() };
      await api.post("/hrm/postings", payload);
      toast.success("Posting drafted — publish it when ready");
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 overflow-y-auto p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={submit}
        className="max-w-3xl mx-auto frost-glass border border-white/12 p-6 my-8" data-testid="hrm-post-create-modal">
        <div className="label-mono">Draft a new posting</div>
        <h2 className="font-display text-2xl mt-2">Job posting</h2>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <Field label="Title" required cls="col-span-2"><input required value={form.title} onChange={e=>setForm({...form, title: e.target.value})} className="w-full px-3 py-2 text-sm" data-testid="post-title"/></Field>
          <Field label="Department" required>
            <select required value={form.department} onChange={e=>setForm({...form, department: e.target.value})} className="w-full px-3 py-2 text-sm">
              {DEPTS.map(([c,l]) => <option key={c} value={c}>{l}</option>)}
            </select>
          </Field>
          <Field label="Position type">
            <select value={form.position_type} onChange={e=>setForm({...form, position_type: e.target.value})} className="w-full px-3 py-2 text-sm">
              <option value="permanent_ft">Permanent FT</option>
              <option value="permanent_pt">Permanent PT</option>
              <option value="term">Term</option>
              <option value="casual">Casual</option>
              <option value="contract">Contract</option>
              <option value="seasonal">Seasonal</option>
            </select>
          </Field>
          <Field label="Hours / week"><input type="number" value={form.hours_per_week ?? ""} onChange={e=>setForm({...form, hours_per_week: parseInt(e.target.value)||null})} className="w-full px-3 py-2 text-sm"/></Field>
          <Field label="Salary range"><input value={form.salary_range} onChange={e=>setForm({...form, salary_range: e.target.value})} placeholder="e.g. $65,000 – $78,000" className="w-full px-3 py-2 text-sm"/></Field>
          <Field label="Location" cls="col-span-2"><input value={form.location} onChange={e=>setForm({...form, location: e.target.value})} className="w-full px-3 py-2 text-sm"/></Field>
          <Field label="Deadline" required cls="col-span-2">
            <input type="datetime-local" required value={form.closes_at} onChange={e=>setForm({...form, closes_at: e.target.value})} className="w-full px-3 py-2 text-sm" data-testid="post-closes"/>
          </Field>
          <Field label="Summary" cls="col-span-2" required>
            <textarea required rows={2} value={form.summary} onChange={e=>setForm({...form, summary: e.target.value})} placeholder="One-line pitch shown on the posting card" className="w-full px-3 py-2 text-sm" data-testid="post-summary"/>
          </Field>
          <Field label="Full description" cls="col-span-2" required>
            <textarea required rows={5} value={form.description} onChange={e=>setForm({...form, description: e.target.value})} className="w-full px-3 py-2 text-sm" data-testid="post-desc"/>
          </Field>
          <Field label="Responsibilities" cls="col-span-2">
            <textarea rows={4} value={form.responsibilities} onChange={e=>setForm({...form, responsibilities: e.target.value})} className="w-full px-3 py-2 text-sm"/>
          </Field>
          <Field label="Qualifications" cls="col-span-2">
            <textarea rows={4} value={form.qualifications} onChange={e=>setForm({...form, qualifications: e.target.value})} className="w-full px-3 py-2 text-sm"/>
          </Field>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={form.require_resume} onChange={e=>setForm({...form, require_resume: e.target.checked})}/>
            Require résumé
          </label>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={form.require_cover_letter} onChange={e=>setForm({...form, require_cover_letter: e.target.checked})}/>
            Require cover letter
          </label>
          <label className="flex items-center gap-2 text-sm text-white/70 col-span-2">
            <input type="checkbox" checked={form.preferred_band_member} onChange={e=>setForm({...form, preferred_band_member: e.target.checked})}/>
            KLFN band members preferred (per s.35 hiring preference)
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-white/8">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={busy} className="btn-cta primary" data-testid="post-submit">
            {busy ? "Saving…" : "Save draft"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PostingDrawer({ isAdmin, pid, onClose, onSelected }) {
  const [post, setPost] = useState(null);
  const [apps, setApps] = useState([]);
  const [openApp, setOpenApp] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    const [p, a] = await Promise.all([
      api.get(`/hrm/postings/${pid}`).then(r=>r.data).catch(()=>null),
      isAdmin ? api.get(`/hrm/postings/${pid}/applications`).then(r=>r.data).catch(()=>[]) : Promise.resolve([]),
    ]);
    setPost(p);
    setApps(a);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid, isAdmin]);
  if (!post) return null;
  const st = POSTING_STATUS_STYLE[post.status] || {};

  const publish = async () => { await api.post(`/hrm/postings/${pid}/publish`); toast.success("Posting is now live"); load(); };
  const closeIt = async () => { await api.post(`/hrm/postings/${pid}/close`); toast.success("Closed for review"); load(); };
  const withdraw = async () => {
    const ok = await confirm({ title: "Withdraw this posting?", destructive: true, message: "All submitted applications will be marked not selected and applicants notified in-portal.", confirmLabel: "Withdraw" });
    if (!ok) return;
    await api.post(`/hrm/postings/${pid}/withdraw`); toast.success("Posting withdrawn"); load();
  };
  const select = async (aid) => {
    const ok = await confirm({ title: "Issue Notice of Selection?", message: "This applicant will be marked selected; all others will be marked not selected. This action is final for the posting.", confirmLabel: "Issue notice" });
    if (!ok) return;
    await api.post(`/hrm/postings/${pid}/select/${aid}`);
    toast.success("Notice of Selection issued");
    load();
    onSelected && onSelected();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 overflow-y-auto p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="max-w-4xl mx-auto frost-glass border border-white/12 my-8 p-8" data-testid="hrm-post-drawer">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <div className="label-mono">{post.department} · {post.position_type?.replace("_"," ")}</div>
            <h2 className="font-display text-3xl mt-2 leading-tight">{post.title}</h2>
            <div className="flex gap-3 mt-3 flex-wrap items-center">
              <span className="tag" style={{color: st.color}}>{st.label}</span>
              <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">
                closes {post.closes_at ? new Date(post.closes_at).toLocaleDateString() : "—"}
              </span>
              <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">
                {apps.length || post.application_count} applications
              </span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && post.status === "draft" && <button onClick={publish} className="btn-cta primary text-xs" data-testid="post-publish"><Send size={12}/> Publish</button>}
            {isAdmin && post.status === "open" && <button onClick={closeIt} className="btn-cta text-xs" data-testid="post-close"><CheckCircle2 size={12}/> Close for selection</button>}
            {isAdmin && ["draft","open","in_selection"].includes(post.status) && <button onClick={withdraw} className="btn-cta danger text-xs" data-testid="post-withdraw"><Trash2 size={12}/> Withdraw</button>}
            <button onClick={onClose} className="btn-cta text-xs"><X size={12}/></button>
          </div>
        </div>

        <div className="mt-6 grid lg:grid-cols-[1.2fr_1fr] gap-6">
          <div>
            <div className="label-mono mb-2">Summary</div>
            <div className="text-white/75 leading-relaxed">{post.summary}</div>

            {post.description && <><div className="label-mono mt-6 mb-2">Description</div><div className="text-white/70 whitespace-pre-wrap leading-relaxed">{post.description}</div></>}
            {post.responsibilities && <><div className="label-mono mt-6 mb-2">Responsibilities</div><div className="text-white/70 whitespace-pre-wrap leading-relaxed">{post.responsibilities}</div></>}
            {post.qualifications && <><div className="label-mono mt-6 mb-2">Qualifications</div><div className="text-white/70 whitespace-pre-wrap leading-relaxed">{post.qualifications}</div></>}
          </div>
          <div>
            <div className="border border-white/8 bg-white/[0.02] p-5">
              <div className="label-mono mb-3">At a glance</div>
              <div className="text-sm space-y-2">
                <div className="flex justify-between"><span className="text-white/50">Type</span><span>{(post.position_type||"").replace("_"," ")}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Hours/wk</span><span>{post.hours_per_week || "—"}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Salary</span><span>{post.salary_range || "—"}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Location</span><span>{post.location}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Résumé</span><span>{post.require_resume ? "Required" : "Optional"}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Cover letter</span><span>{post.require_cover_letter ? "Required" : "Optional"}</span></div>
                {post.preferred_band_member && (
                  <div className="pt-3 border-t border-white/8 text-[color:var(--cobalt)] text-xs">KLFN band members preferred.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Applications */}
        {isAdmin && (
          <div className="mt-10">
            <div className="label-mono mb-3">Applications ({apps.length})</div>
            {apps.length === 0 ? (
              <div className="font-mono text-xs text-white/40">No applications submitted yet.</div>
            ) : (
              <div className="border border-white/8">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.03]">
                    <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50">
                      <th className="px-4 py-3">Applicant</th>
                      <th className="px-4 py-3">Submitted</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3"/>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map(a => {
                      const ast = APP_STATUS_STYLE[a.status] || {};
                      return (
                        <tr key={a.id} className="border-t border-white/8">
                          <td className="px-4 py-3">
                            <div className="font-display">{a.applicant_name}</div>
                            <div className="font-mono text-[10px] text-white/50">{a.applicant_email}{a.is_band_member && " · band member"}</div>
                          </td>
                          <td className="px-4 py-3 font-mono text-[10px] text-white/50">{new Date(a.submitted_at).toLocaleString()}</td>
                          <td className="px-4 py-3"><span className="tag" style={{color: ast.color}}>{ast.label}</span></td>
                          <td className="px-4 py-3 flex gap-2">
                            <button onClick={()=>setOpenApp(a)} className="btn-cta text-xs" data-testid={`app-view-${a.id}`}>Review</button>
                            {["submitted","shortlisted"].includes(a.status) && ["open","in_selection"].includes(post.status) && (
                              <button onClick={()=>select(a.id)} className="btn-cta primary text-xs" data-testid={`app-select-${a.id}`}><Award size={12}/> Select</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {openApp && <ApplicationDetailModal app={openApp} onClose={()=>{setOpenApp(null); load();}}/>}
      </div>
    </div>
  );
}

function ApplicationDetailModal({ app, onClose }) {
  const [notes, setNotes] = useState(app.reviewer_notes || "");
  const [status, setStatus] = useState(app.status);
  const save = async () => {
    await api.patch(`/hrm/applications/${app.id}`, { reviewer_notes: notes, status });
    toast.success("Application updated");
    onClose();
  };
  const ast = APP_STATUS_STYLE[app.status] || {};
  return (
    <div className="fixed inset-0 z-[60] bg-black/90 overflow-y-auto p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="max-w-2xl mx-auto frost-glass border border-white/12 my-8 p-6" data-testid="hrm-app-modal">
        <div className="flex justify-between items-start">
          <div>
            <div className="label-mono">Application</div>
            <h2 className="font-display text-2xl mt-2">{app.applicant_name}</h2>
            <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">
              {app.applicant_email} · {app.applicant_phone || "no phone"}
              {app.is_band_member && " · band member"}
            </div>
          </div>
          <span className="tag" style={{color: ast.color}}>{ast.label}</span>
        </div>

        {app.resume_url && (
          <div className="mt-5">
            <div className="label-mono mb-2">Résumé</div>
            <a href={app.resume_url.startsWith("http") ? app.resume_url : `${API.replace("/api", "")}${app.resume_url}`}
              target="_blank" rel="noopener noreferrer" className="btn-cta text-xs" data-testid="app-resume-view">
              <FileDown size={12}/> Open résumé
            </a>
          </div>
        )}
        {app.cover_letter && (
          <div className="mt-5">
            <div className="label-mono mb-2">Cover letter</div>
            <div className="border border-white/8 bg-white/[0.02] p-4 text-sm whitespace-pre-wrap text-white/80">{app.cover_letter}</div>
          </div>
        )}
        {app.cover_letter_url && (
          <div className="mt-5">
            <div className="label-mono mb-2">Cover letter (attached file)</div>
            <a href={app.cover_letter_url.startsWith("http") ? app.cover_letter_url : `${API.replace("/api", "")}${app.cover_letter_url}`}
              target="_blank" rel="noopener noreferrer" className="btn-cta text-xs">
              <FileDown size={12}/> Open cover letter
            </a>
          </div>
        )}
        {(app.answers || []).length > 0 && (
          <div className="mt-5">
            <div className="label-mono mb-2">Answers</div>
            {(app.answers || []).map((a,i) => (
              <div key={i} className="border border-white/8 p-3 mb-2 text-sm">
                <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest">{a.prompt || a.id}</div>
                <div className="text-white/85 mt-1 whitespace-pre-wrap">{String(a.value || "—")}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5">
          <div className="label-mono mb-2">Reviewer notes (HR only)</div>
          <textarea rows={3} value={notes} onChange={e=>setNotes(e.target.value)} className="w-full px-3 py-2 text-sm" data-testid="app-notes"/>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Field label="Status">
            <select value={status} onChange={e=>setStatus(e.target.value)} className="w-full px-3 py-2 text-sm" data-testid="app-status">
              <option value="submitted">Submitted</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="not_selected">Not selected</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-white/8">
          <button onClick={onClose} className="btn-cta">Close</button>
          <button onClick={save} className="btn-cta primary" data-testid="app-save"><Send size={12}/> Save</button>
        </div>
      </div>
    </div>
  );
}

/* ================= APPLICATIONS TAB ================= */

function ApplicationsTab({ applications, onOpen }) {
  const [q, setQ] = useState("");
  const filtered = q ? applications.filter(a => (a.applicant_name || "").toLowerCase().includes(q.toLowerCase()) || (a.posting_title || "").toLowerCase().includes(q.toLowerCase())) : applications;
  return (
    <div className="mt-8">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search applicant or posting"
          className="px-3 py-2 text-sm w-72" data-testid="hrm-app-search"/>
      </div>
      {filtered.length === 0 ? (
        <div className="border border-white/8 bg-white/[0.015] p-8 text-center">
          <div className="font-display text-xl">No applications yet.</div>
          <div className="text-white/50 text-sm mt-2">Applications show up here as candidates submit them against open postings.</div>
        </div>
      ) : (
        <div className="border border-white/8">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03]">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50">
                <th className="px-4 py-3">Applicant</th>
                <th className="px-4 py-3">Posting</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"/>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => {
                const ast = APP_STATUS_STYLE[a.status] || {};
                return (
                  <tr key={a.id} className="border-t border-white/8 hover:bg-white/[0.02] cursor-pointer" onClick={()=>onOpen(a)}>
                    <td className="px-4 py-3">
                      <div className="font-display">{a.applicant_name}</div>
                      <div className="font-mono text-[10px] text-white/50">{a.applicant_email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-white/70 truncate max-w-xs">{a.posting_title}</td>
                    <td className="px-4 py-3 font-mono text-[10px] text-white/50">{new Date(a.submitted_at).toLocaleString()}</td>
                    <td className="px-4 py-3"><span className="tag" style={{color: ast.color}}>{ast.label}</span></td>
                    <td className="px-4 py-3"><ChevronRight size={14} className="text-white/30"/></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ================= ONBOARDING TAB ================= */

function OnboardingTab({ onboardings, onOpen }) {
  return (
    <div className="mt-8">
      {onboardings.length === 0 ? (
        <div className="border border-white/8 bg-white/[0.015] p-8 text-center">
          <div className="font-display text-xl">No onboarding packages yet.</div>
          <div className="text-white/50 text-sm mt-2">
            Onboarding starts automatically after a Notice of Selection, or you can open one from an employee folder.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {onboardings.map(o => {
            const total = o.items.length;
            const done = o.items.filter(i => i.completed).length;
            const pct = Math.round((done / total) * 100);
            const st = o.status === "completed" ? { label: "Complete", color: "#89b4fa" }
              : o.status === "in_progress" ? { label: "In progress", color: "#d4b876" }
              : { label: "Pending", color: "#a6adc8" };
            return (
              <button key={o.id} onClick={()=>onOpen(o.id)} className="border border-white/8 bg-white/[0.015] p-5 text-left" data-testid={`onb-card-${o.id}`}>
                <div className="flex items-center justify-between">
                  <span className="tag" style={{color: st.color}}>{st.label}</span>
                  <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{done}/{total}</span>
                </div>
                <div className="font-display text-lg mt-3">{o.employee_name}</div>
                <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">
                  issued {new Date(o.issued_at).toLocaleDateString()} by {o.issued_by_name}
                </div>
                <div className="h-0.5 bg-white/8 mt-4"><div className="h-full bg-[color:var(--cobalt)]" style={{width: `${pct}%`}}/></div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OnboardingDrawer({ isAdmin, oid, onClose }) {
  const [ob, setOb] = useState(null);
  const load = () => api.get(`/hrm/onboarding/${oid}`).then(r=>setOb(r.data)).catch(()=>{});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [oid]);
  if (!ob) return null;

  const toggle = async (iid, completed) => {
    await api.patch(`/hrm/onboarding/${oid}/items/${iid}`, { completed });
    load();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 overflow-y-auto p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="max-w-2xl mx-auto frost-glass border border-white/12 my-8 p-8" data-testid="hrm-onb-drawer">
        <div className="flex justify-between">
          <div>
            <div className="label-mono">Onboarding package</div>
            <h2 className="font-display text-2xl mt-2">{ob.employee_name}</h2>
            <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">
              Issued {new Date(ob.issued_at).toLocaleDateString()} · {ob.status.replace("_"," ")}
            </div>
          </div>
          <button onClick={onClose} className="btn-cta text-xs"><X size={12}/></button>
        </div>

        <div className="mt-6 space-y-2">
          {ob.items.map(item => (
            <div key={item.id} className={`flex items-center gap-3 border p-3 ${item.completed ? "border-[color:var(--cobalt)]/50 bg-[color:var(--cobalt)]/[0.04]" : "border-white/8"}`}>
              <button
                onClick={()=>isAdmin && toggle(item.id, !item.completed)}
                disabled={!isAdmin}
                data-testid={`onb-item-${item.id}`}
                className={`w-5 h-5 flex-shrink-0 border ${item.completed ? "bg-[color:var(--cobalt)] border-[color:var(--cobalt)] text-white" : "border-white/30"} flex items-center justify-center`}
              >
                {item.completed && <CheckCircle2 size={12}/>}
              </button>
              <div className="flex-1">
                <div className="text-white/90">{item.label}</div>
                <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest">
                  {item.kind}{item.required ? " · required" : ""}
                  {item.completed_at && ` · completed ${new Date(item.completed_at).toLocaleDateString()}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ================= Shared Field ================= */

function Field({ label, required, cls, locked, children }) {
  return (
    <label className={`block ${cls || ""}`}>
      <div className="label-mono mb-1 flex items-center gap-2">
        {label}{required && <span className="text-[color:var(--signal)]">*</span>}
        {locked && <span className="text-white/30">(view only)</span>}
      </div>
      {children}
    </label>
  );
}
